import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import {message, Pagination, Popconfirm, Table} from 'antd';
import { withWindowSize } from 'air-with-window-size';
import moment from 'moment';
import PropTypes from "prop-types";
import './OrdersTable.css';
import {EditIcon, DeleteIcon} from "../../../../common/icons";
import EditOrder from "./EditOrder";

const pageSizeDefault = 7;

@inject('commonStore', 'translationsStore', 'ordersStore')
@observer
class OrdersTable extends Component {
  constructor(props) {
    super(props);
    this.defaultPageSize = 1;
    const dataSource = this.buildDataSourceWithKeys(this.props.orders);
    const pageSize = this.defaultPageSize;

    let pagination = false;
    let paginationText = '';
    this.state = {
      dataSourceBuffer: dataSource,
      selectedRowKeys: [],
      pageSize,
      pagination,
      paginationText
    };

    this.EditOrderRef = React.createRef();
  }

  static contextTypes = {
    router: PropTypes.object
  };

  componentDidMount = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.orders);
    if (!isEqual(dataSource, this.state.dataSourceBuffer) || (dataSource.length > 0 && !this.state.pagination)) {
      this.buildPagination();
    }

    this.EditOrderinstance = this.EditOrderRef.current.wrappedInstance;
  };

  componentDidUpdate(prevProps) {
    const someSizeChanged = prevProps.componentHeight !== this.props.componentHeight || prevProps.windowHeight !== this.props.windowHeight;
    const dataSource = this.buildDataSourceWithKeys(this.props.orders);
    if (someSizeChanged || !isEqual(dataSource, this.state.dataSourceBuffer) || (dataSource.length > 0 && !this.state.pagination)) {
      this.buildPagination();
    }
  }

  helperPageSize = () => {
    const defaultDocHeight = 800;
    if (this.props.documentHeight === defaultDocHeight) {
      return pageSizeDefault;
    } else {
      return Math.floor((this.props.documentHeight * pageSizeDefault) / defaultDocHeight);
    }
  };

  changePageSize = pageSize => {
    this.setState({ pageSize });
  };

  helperPaginationText = (start, end, length) => {
    const { translationsStore } = this.props;
    if (length === 0) return `0 ${translationsStore.translate('orders')}`;
    else if (length === 1) return `1 ${translationsStore.translate('orders')}`;
    else return `${start}-${end} ${translationsStore.translate('of')} ${length} ${translationsStore.translate('orders')}`;
  };

  buildDataSourceWithKeys = collection => collection.map((item, index) => ({ ...item, key: index }));

  buildPagination = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.orders);
    const pageSize = this.helperPageSize();
    const pagination = {
      total: dataSource.length,
      defaultCurrent: 1,
      current: 1,
      // hideOnSinglePage: true,
      pageSize,
      simple: true,
      size: 'small',
      onChange: this.onPageChange
    };
    const start = 1;
    const end = Math.min(pagination.pageSize, pagination.total);

    const paginationText = this.helperPaginationText(start, end, dataSource.length);
    this.setState({
      dataSourceBuffer: dataSource,
      pageSize,
      pagination: { ...pagination },
      paginationText
    });
  };

  onPageChange = (page, pageSize) => {
    const dataSource = this.buildDataSourceWithKeys(this.props.orders);
    const { pagination } = this.state;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, pagination.total);
    let paginationText = this.helperPaginationText(start, end, dataSource.length);
    this.setState({
      dataSourceBuffer: dataSource,
      pagination: { ...pagination, current: page },
      paginationText
    });
  };

  deleteOrder = order => {
    this.props.ordersStore.deleteOrder(order).then(() => {
        message.success(this.props.translationsStore.translate('order.orderTable.deleted'));
    }).catch(() => {
      message.error(this.props.translationsStore.translate('order.orderTable.deleteError'));
    });
  };

  render() {
    const { orders, commonStore, translationsStore } = this.props;

    const dataSource = this.buildDataSourceWithKeys(orders);

    const columns = [
      {
        key: 'leadId',
        dataIndex: 'leadid',
        title: translationsStore.translate('order.orderTable.headerLabels.leadId'),
        render: (text, record) => record.leadId
      },
      {
        key: 'externalOfferId',
        dataIndex: 'externalOfferId',
        title: translationsStore.translate('order.orderTable.headerLabels.externalOfferId'),
        render: (text, record) => record.externalOfferId
      },
      {
        key: 'externalOrderId',
        dataIndex: 'externalOrderId',
        title: translationsStore.translate('order.orderTable.headerLabels.externalOrderId'),
        render: (text, record) => record.externalOrderId
      },
      {
        key: 'vehicle',
        dataIndex: 'vehicle',
        align: 'center',
        title: translationsStore.translate('order.orderTable.headerLabels.assignedVehicle'),
        render: (text, record) => record.car.plate
      },
      {
        key: 'state',
        dataIndex: 'state',
        title: translationsStore.translate('order.orderTable.headerLabels.state'),
        render: (text, record) => translationsStore.translate('order.state.' + record.state)
      },
      {
        key: 'status',
        dataIndex: 'status',
        title: translationsStore.translate('order.orderTable.headerLabels.status'),
        render: (text, record) => <span className={record.status}><span>{translationsStore.translate('order.status.' + record.status)}</span>
          <span> {record.status === "ACCEPTED" && record.acceptedAt ? moment(record.acceptedAt).format(commonStore.longDateFormat.L) : ''}
          {record.status === "DECLINED" && record.declinedAt ? moment(record.declinedAt).format(commonStore.longDateFormat.L) : ''}
          </span>
          </span>
      },
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        align: 'center',
        className: 'ColumnCentered',
        title: translationsStore.translate('order.orderTable.headerLabels.createdAt'),
        render: (text, record) => <span>{record.createdAt ? moment(record.createdAt).format(commonStore.longDateFormat.L) : '-'}</span>,
      },
      {
        key: 'lastUpdatedAt',
        dataIndex: 'lastUpdatedAt',
        align: 'center',
        className: 'ColumnCentered',
        title: translationsStore.translate('order.orderTable.headerLabels.lastUpdatedAt'),
        render: (text, record) => <span>{record.updatedAt ? moment(record.updatedAt).format(commonStore.longDateFormat.L) : '-'}</span>,
      },
      {
        render: (text, record) => {
          return (
            <div className="buttonsContainer">
              <EditIcon onClick={() => this.EditOrderinstance.show(record) }/>
              {(record.status === "CREATED" &&
                <Popconfirm
                  title={translationsStore.translate('order.orderTable.deleteConfirm')}
                  onConfirm={() => this.deleteOrder(record)}
                  okText={translationsStore.translate('order.orderTable.deleteConfirmYes')}
                  cancelText={translationsStore.translate('order.orderTable.deleteConfirmNo')}
                  placement={'left'}
                >
                  <DeleteIcon className={'DeleteIcon'}/>
                </Popconfirm>
              )}
            </div>
          );
        }
      }
    ];

    return (
      <div className="OrdersTable">
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={this.state.pagination}
          rowClassName="OrderRow"
        />
        {!commonStore.loading && (
          <div className="OrdersTablePagination">
            <div>{this.state.paginationText}</div>
            {dataSource.length > this.helperPageSize() && <Pagination {...this.state.pagination} />}
          </div>
        )}
        <EditOrder ref={this.EditOrderRef} width={560} />
      </div>
    );
  }
}

export default withWindowSize(OrdersTable);



// WEBPACK FOOTER //
// ./src/components/App/OrdersManagerDashboard/OrdersTable/OrdersTable.js